export type SortDir = 'asc' | 'desc';
export type SortValue = string | number | null | undefined;

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

export function filterRows<T>(rows: T[], query: string, text: (row: T) => string | undefined): T[] {
  const q = query.trim().toLowerCase();
  return q ? rows.filter((r) => (text(r) ?? '').toLowerCase().includes(q)) : rows;
}

const isNil = (v: SortValue) => v == null || (typeof v === 'number' && Number.isNaN(v));

function compare(x: string | number, y: string | number): number {
  if (typeof x === 'number' && typeof y === 'number') return x < y ? -1 : x > y ? 1 : 0;
  return collator.compare(String(x), String(y));
}

/** Stable, non-mutating. Nullish/NaN values sort last in both directions. */
export function sortRows<T>(rows: T[], by: (row: T) => SortValue, dir: SortDir): T[] {
  const sign = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const x = by(a), y = by(b);
    const xn = isNil(x), yn = isNil(y);
    if (xn || yn) return xn === yn ? 0 : xn ? 1 : -1;
    return compare(x as string | number, y as string | number) * sign;
  });
}

export function paginate<T>(rows: T[], page: number, pageSize: number) {
  const size = Math.max(1, Math.floor(pageSize) || 1);
  const pageCount = Math.max(1, Math.ceil(rows.length / size));
  const want = Number.isFinite(page) ? page : 1;
  const p = Math.min(Math.max(1, want), pageCount);
  return { items: rows.slice((p - 1) * size, p * size), pageCount, page: p };
}
